import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import type { Post } from '../types'
import { ProductCollaborationPanel } from './ProductCollaborationPanel'

interface ContextPanelProps {
  post?: Post
}

export function ContextPanel({ post }: ContextPanelProps) {
  const mediaAttachments = post?.media || []
  const annotations = post?.annotations || []
  const references = post?.references || []

  // Also extract inline images from markdown content
  const inlineImages: { url: string; alt: string }[] = []
  if (post?.content) {
    const matches = post.content.matchAll(/!\[([^\]]*)\]\(([^)]+)\)/g)
    for (const m of matches) {
      const url = m[2]
      if (url.startsWith('http')) {
        inlineImages.push({ alt: m[1] || 'Image', url })
      }
    }
  }

  // Combine: media attachments + inline images (deduplicated)
  const allMedia = [
    ...mediaAttachments.map(m => ({ id: `media-${m.id}`, url: m.media_url, label: m.caption || m.role || 'media' })),
    ...inlineImages
      .filter(img => !mediaAttachments.some(m => m.media_url === img.url))
      .map((img, i) => ({ id: `inline-${i}`, url: img.url, label: img.alt })),
  ]

  const [tab, setTab] = useState<'products' | 'references' | 'media' | 'annotations' | 'changes' | 'translations'>('media')

  const { data: revisions = [] } = useQuery({
    queryKey: ['revisions', post?.id],
    queryFn: () => apiClient.listRevisions(post!.id),
    enabled: !!post?.id,
    staleTime: 30_000,
  })

  const {
    data: productStatus,
    isLoading: productStatusLoading,
    error: productStatusError,
  } = useQuery({
    queryKey: ['productCollaborationStatus', post?.id],
    queryFn: () => apiClient.getPostProductCollaborationStatuses(post!.id),
    enabled: !!post?.id && !!post.collab_enabled,
    staleTime: 3_000,
    refetchInterval: 5_000,
  })
  const products = productStatus?.products || []
  const tabLabels = {
    products: 'Object',
    media: 'Media',
    notes: 'Notes',
    changes: 'Changes',
    refs: 'Refs',
    lang: 'Lang',
  } as const

  return (
    <aside className="context-panel">
      <div className="context-tabs">
        {(['products', 'media', 'notes', 'changes', 'refs', 'lang'] as const)
          .filter(t => t !== 'products' || !!post?.collab_enabled)
          .map(t => {
          const tabKey = t === 'products' ? 'products' : t === 'notes' ? 'annotations' : t === 'refs' ? 'references' : t === 'lang' ? 'translations' : t
          const count = t === 'products' ? products.length : t === 'media' ? allMedia.length : t === 'notes' ? annotations.length : t === 'refs' ? references.length : t === 'changes' ? revisions.length : 0
          return (
          <button
            key={t}
            className={`context-tab ${tab === tabKey ? 'context-tab--active' : ''}`}
            onClick={() => setTab(tabKey as typeof tab)}
          >
            {tabLabels[t]}
            {count > 0 && <span style={{ marginLeft: 3, fontSize: 9, color: 'var(--text-muted)' }}>({count})</span>}
          </button>
          )
        })}
      </div>

      <div className="context-body">
        {tab === 'products' && (
          <ProductCollaborationPanel
            products={products}
            isLoading={productStatusLoading}
            error={(productStatusError as Error) || null}
          />
        )}

        {tab === 'references' && (
          <>
            <div className="sidebar-label" style={{ marginBottom: 12 }}>
              <i className="fas fa-link" style={{ marginRight: 6 }} />
              References ({references.length})
            </div>
            {references.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No references yet.</p>
            )}
            {references.map((ref) => (
              <div key={ref.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{ref.title}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {ref.authors && <span>{ref.authors}</span>}
                  {ref.year && <span> · {ref.year}</span>}
                </div>
                {ref.url && (
                  <a href={ref.url} target="_blank" rel="noopener" style={{ fontSize: 11, color: 'var(--accent)' }}>
                    <i className="fas fa-external-link-alt" style={{ marginRight: 4 }} />{ref.url.replace(/https?:\/\//, '').slice(0, 30)}
                  </a>
                )}
              </div>
            ))}
          </>
        )}

        {tab === 'media' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="sidebar-label" style={{ margin: 0 }}>
                <i className="fas fa-images" style={{ marginRight: 6 }} />
                Media ({allMedia.length})
              </div>
              <button className="topbar-btn" style={{ fontSize: 11, padding: '3px 8px' }}>
                <i className="fas fa-upload" style={{ marginRight: 4 }} /> Upload
              </button>
            </div>
            {allMedia.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No media found.</p>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {allMedia.map((m) => (
                <div key={m.id} style={{
                  aspectRatio: '4/3', background: 'var(--bg-light)', border: '1px solid var(--border)',
                  overflow: 'hidden', cursor: 'pointer', position: 'relative',
                }}>
                  <img
                    src={m.url}
                    alt={m.label}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      const el = e.target as HTMLImageElement
                      el.style.display = 'none'
                      el.parentElement!.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:4px;color:var(--text-muted);font-size:10px"><i class="fas fa-image" style="font-size:20px;opacity:0.3"></i>${m.label}</div>`
                    }}
                  />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '2px 4px', background: 'rgba(0,0,0,0.6)',
                    color: '#fff', fontSize: 9, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
                  }}>
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'annotations' && (
          <>
            <div className="sidebar-label" style={{ marginBottom: 12 }}>
              <i className="fas fa-comments" style={{ marginRight: 6 }} />
              Annotations ({annotations.length})
            </div>
            {annotations.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No annotations yet.</p>
            )}
            {annotations.map((a) => (
              <div key={a.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{
                    width: 24, height: 24, background: 'var(--accent)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
                  }}>
                    {(a.author_name || '?')[0]}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{a.author_name}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    · {new Date(a.created_at).toLocaleDateString('de-AT')}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', paddingLeft: 32 }}>{a.note_text}</div>
                {a.replies && a.replies.length > 0 && (
                  <div style={{ paddingLeft: 32, marginTop: 8 }}>
                    {a.replies.map(r => (
                      <div key={r.id} style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                        <b>{r.author_name}</b>: {r.note_text}
                      </div>
                    ))}
                  </div>
                )}
                <button style={{
                  fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none',
                  cursor: 'pointer', paddingLeft: 32, marginTop: 4,
                }}>
                  <i className="fas fa-reply" style={{ marginRight: 4 }} /> Reply
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <input type="text" placeholder="Add a comment..." style={{
                flex: 1, padding: '6px 8px', border: 'none', borderBottom: '1px solid var(--border)',
                fontSize: 13, fontFamily: 'var(--font-body)', outline: 'none',
              }} />
            </div>
          </>
        )}

        {tab === 'changes' && (
          <>
            <div className="sidebar-label" style={{ marginBottom: 12 }}>
              <i className="fas fa-history" style={{ marginRight: 6 }} />
              Changes ({revisions.length})
            </div>
            {revisions.length === 0 && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>No changes recorded.</p>
            )}
            {revisions.map((r) => (
              <div key={r.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="fas fa-code-branch" style={{ fontSize: 10, color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{r.author_name || 'System'}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                    {new Date(r.created_at).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.content_preview?.slice(0, 80) || '(empty)'}
                </div>
              </div>
            ))}
          </>
        )}

        {tab === 'translations' && post && (
          <TranslationsTab post={post} />
        )}
      </div>
    </aside>
  )
}

const LANG_NAMES: Record<string, string> = { de: 'Deutsch', en: 'English', sl: 'Slovenščina', it: 'Italiano', es: 'Español' }

function TranslationsTab({ post }: { post: Post }) {
  const queryClient = useQueryClient()
  const [translating, setTranslating] = useState<string | null>(null)

  const { data: langData } = useQuery({
    queryKey: ['post-languages', post.id],
    queryFn: () => apiClient.getPostLanguages(post.id),
    staleTime: 30_000,
  })

  const variants = langData?.variants || []
  const currentLang = post.language || 'de'

  const handleTranslate = async (targetLang: string) => {
    setTranslating(targetLang)
    try {
      await apiClient.translatePost(post.id, [targetLang], false)
      queryClient.invalidateQueries({ queryKey: ['post-languages', post.id] })
    } catch (err) {
      alert(`Translation failed: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setTranslating(null)
    }
  }

  return (
    <>
      <div className="sidebar-label" style={{ marginBottom: 12 }}>
        <i className="fas fa-language" style={{ marginRight: 6 }} />
        Language Variants
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>
        Current: <strong>{currentLang.toUpperCase()}</strong> ({LANG_NAMES[currentLang] || currentLang})
      </div>
      {['de', 'en', 'sl', 'it', 'es'].map(lang => {
        const variant = variants.find(v => v.language === lang)
        const isCurrent = lang === currentLang
        const exists = !!variant

        return (
          <div key={lang} className="sidebar-item" style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', gap: 8 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, width: 24 }}>{lang.toUpperCase()}</span>
            <div style={{ flex: 1, fontSize: 13 }}>
              {LANG_NAMES[lang] || lang}
              {variant?.auto_translated && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>(auto)</span>}
            </div>
            {isCurrent ? (
              <span style={{ fontSize: 10, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-success)', border: '1px solid var(--color-success)' }}>
                Current
              </span>
            ) : exists ? (
              <a href={`/post/${variant!.post_id}`} style={{ fontSize: 10, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-info)', border: '1px solid var(--color-info)', textDecoration: 'none' }}>
                Open
              </a>
            ) : (
              <button
                onClick={() => handleTranslate(lang)}
                disabled={translating === lang}
                style={{
                  fontSize: 10, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.04em',
                  border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--text-muted)', cursor: 'pointer',
                }}
              >
                {translating === lang ? <i className="fas fa-spinner fa-spin" /> : 'Generate'}
              </button>
            )}
          </div>
        )
      })}
    </>
  )
}
